<template>
  <FriedModalSubContainer>
    <template #header>
      <FriedModalHeader
        :header="$t('invite-recipient')"
        :description="$t('please-enter-recipient-details')"
      />
    </template>
    <div class="invite-modal">
      <FriedMessage
        v-if="showWarning"
        type="warning"
        class="warning-message full-width"
        :title="warningTitle"
      >
        <FriedParagraph>{{ warningDescription }}</FriedParagraph>
        <template #button>
          <FriedButton
            button-type="secondary"
            data-external="invite-recipient-modal-transfer-button"
            @click="$emit('transfer-to-existing', matchedExistingRecipient)"
          >
            {{ $t('change_signer') }}
          </FriedButton>
        </template>
      </FriedMessage>
      <FriedH5 class="full-width">{{ $t('cr_invite_modal_contact_info') }}</FriedH5>
      <FriedInput
        v-model.trim="formData.email"
        data-external="invite-modal-email-input"
        type="email"
        :label="$t('email') + '*'"
        required
        :error-message="showWarning ? '' : emailError"
        @blur="handleBlur('email')"
        @update:model-value="handleUpdate('email')"
      />
      <FriedInput
        v-model.trim="formData.mobile"
        data-external="invite-modal-telephone-input"
        type="tel"
        :label="$t('mobile-number') + ' (+46701234567)'"
        :error-message="showWarning ? '' : mobileError"
        @blur="handleBlur('mobile')"
        @update:model-value="handleUpdate('mobile')"
      />
      <FriedH5 class="full-width">{{ $t('cr_invite_modal_name') }}</FriedH5>
      <FriedInput
        v-model.trim="formData.firstName"
        data-external="invite-modal-first-name-input"
        :label="$t('first-name') + '*'"
        required
        :error-message="result.firstName.errorMessage"
        @blur="handleBlur('firstName')"
      />
      <FriedInput
        v-model.trim="formData.lastName"
        data-external="invite-modal-last-name-input"
        :label="$t('last-name') + '*'"
        required
        :error-message="result.lastName.errorMessage"
        @blur="handleBlur('lastName')"
      />
      <FriedH5 class="full-width">{{ $t('cr_invite_modal_company_info') }}</FriedH5>
      <FriedInput
        v-model.trim="formData.companyName"
        data-external="invite-modal-company-name-input"
        :label="$t('company-name') + '*'"
        required
        :error-message="result.companyName.errorMessage"
        @blur="handleBlur('companyName')"
      />
      <FriedInput
        v-model.trim="formData.companyNumber"
        data-external="invite-modal-company-number-input"
        :label="$t('business-registration-number')"
        required
      />
      <FriedInput
        v-model.trim="formData.title"
        data-external="invite-modal-title-input"
        :label="$t('title') + '*'"
        required
        :error-message="result.title.errorMessage"
        @blur="handleBlur('title')"
      />
      <div v-if="showRoleTransfer" class="transfer-container full-width">
        <FriedCheckbox
          v-model="selectedTransferOption"
          :label="$t('transfer_checkbox_label')"
          :disabled="hasSignatureElement"
          data-external="upload-attachment-add-to-library-checkbox"
        />
        <InformationPopper
          :information-text="$t('transfer_checkbox_info_text')"
          trigger-button-data-external="contacts-information-show-button"
        />
      </div>
    </div>
    <template #footer>
      <FriedModalButtonsFooter>
        <FriedButton
          data-external="invite-cancel-button"
          button-type="secondary"
          :disabled="loading"
          @click="$emit('close')"
        >
          {{ $t('cancel') }}
        </FriedButton>
        <FriedButton
          :disabled="!valid"
          :loading="loading"
          data-external="invite-modal-invite-btn"
          class="invite-button"
          @click="handleInvite"
        >
          {{ $t('invite') }}
        </FriedButton>
      </FriedModalButtonsFooter>
    </template>
  </FriedModalSubContainer>
</template>

<script lang="ts">
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import type {
  InviteRecipientData,
  InviteRequiredFields,
  SigningPageRecipient,
} from '@getaccept/lib-shared/src/types/signing-page-recipient';
import type { PropType } from 'vue';
import { computed, ref, defineComponent } from 'vue';
import InformationPopper from '@getaccept/lib-shared/src/components/InformationPopper.vue';
import { RecipientsHelper } from '@getaccept/lib-shared/src/recipients/helpers/recipients.helper';
import { InviteInputType } from '../types/enums/invite-input-type';
import { useValidateRecipient } from '../composables/validate-recipient.composable';

export default defineComponent({
  components: { InformationPopper },
  props: {
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
      default: () => ({}),
    },
    recipients: {
      type: Array as PropType<SigningPageRecipient[]>,
      default: () => [],
    },
    loading: Boolean,
    isSigner: Boolean,
    showRoleTransfer: Boolean,
    hasSignatureElement: Boolean,
    isChangeSigner: Boolean,
  },
  emits: ['invite', 'transfer', 'transfer-to-existing', 'close'],
  setup(props, { emit }) {
    const selectedTransferOption = ref(props.isChangeSigner);

    const formData = ref<InviteRecipientData>({
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      title: '',
      companyName: '',
      companyNumber: '',
      ...props.recipient,
    });

    const {
      checkDuplicate,
      isRecipientAlreadyAdded,
      matchedExistingRecipient,
      warningTitle,
      warningDescription,
      valid,
      result,
      emailError,
      mobileError,
      reset,
      validate,
      validateAll,
    } = useValidateRecipient(
      formData,
      computed(() => props.recipients)
    );

    const showWarning = computed(
      () =>
        !props.hasSignatureElement &&
        isRecipientAlreadyAdded.value &&
        props.isSigner &&
        matchedExistingRecipient.value?.role === RecipientRole.CC
    );

    const handleUpdate = (field: InviteRequiredFields) => {
      if (result.value[field].errorMessage) {
        reset(field);
      }
      switch (field) {
        case 'mobile':
        case 'email':
          checkDuplicate(field);
          break;
        default:
          break;
      }
    };

    const shouldAutoFillNameInputs = computed(
      () =>
        !!formData.value?.email &&
        !emailError.value &&
        !formData.value.firstName &&
        !formData.value.lastName
    );

    const handleBlur = (field: InviteRequiredFields) => {
      if (formData.value[field] || field === 'mobile') {
        validate(field);
      }

      if (field !== 'email') return;

      if (shouldAutoFillNameInputs.value) {
        formData.value = {
          ...formData.value,
          ...RecipientsHelper.getRecipientDataFromEmail(formData.value.email),
        };
      }
    };

    const handleInvite = async () => {
      validateAll();
      if (!valid.value || isRecipientAlreadyAdded.value) return;

      if (selectedTransferOption.value) {
        emit('transfer', { ...formData.value, transferSignature: true });
      } else {
        emit('invite', formData.value);
      }
    };

    return {
      formData,
      InviteInputType,
      result,
      valid,
      validate,
      handleInvite,
      handleUpdate,
      handleBlur,
      matchedExistingRecipient,
      isRecipientAlreadyAdded,
      warningTitle,
      warningDescription,
      showWarning,
      emailError,
      mobileError,
      selectedTransferOption,
    };
  },
});
</script>
<style lang="scss" scoped>
.warning-message-content {
  display: flex;
  gap: var(--spacing-100);
  justify-content: space-between;
}

.invite-modal {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-50);

  .full-width {
    grid-column: 1 / -1;

    &:not(:first-child) {
      margin-top: var(--spacing-75);
    }
  }

  .transfer-container {
    display: flex;
    gap: var(--spacing-25);
    align-items: center;
  }
}
</style>
